import L from "leaflet";
import { POPUP_REOPEN_DELAY } from "../constants";

/**
 * Open a marker’s popup without triggering autoPan.
 * Temporarily disables autoPan to prevent the map from panning when opening the popup.
 */
export function openPopupWithoutPan(marker: L.Marker) {
  const popup: any = (marker as any).getPopup?.() ?? (marker as any)._popup;
  if (!popup) return;
  const prev = popup.options.autoPan;
  popup.options.autoPan = false;
  marker.openPopup();
  setTimeout(() => {
    popup.options.autoPan = prev;
  }, 0);
}

/**
 * Bring the marker’s popup DOM to the front so it stacks above others.
 * Moves the popup element to the end of the popup pane for correct stacking.
 */
export function bringPopupToFront(marker: L.Marker, map: L.Map) {
  const popup: any = (marker as any).getPopup?.() ?? (marker as any)._popup;
  if (!popup) return;
  const el: HTMLElement | null =
    popup.getElement?.() ?? (popup._container as HTMLElement | null);
  const pane: HTMLElement | undefined =
    (map as any)?._panes?.popupPane ?? map.getPanes?.().popupPane;
  if (el && pane && el.parentElement === pane && pane.lastElementChild !== el) {
    pane.appendChild(el);
  }
}

/**
 * Ensure the popup stays visually on top when opened or interacted with.
 * Attaches listeners on popupopen/popupclose; cleans itself up on close.
 */
export function attachPopupFrontHandlers(marker: L.Marker, map: L.Map) {
  let onDown: ((e: MouseEvent) => void) | undefined;

  // Handler for popup open: bring to front and attach mouse event
  const onOpen = () => {
    bringPopupToFront(marker, map);
    const popup: any = (marker as any).getPopup?.() ?? (marker as any)._popup;
    const el: HTMLElement | null =
      popup?.getElement?.() ?? (popup?._container as HTMLElement | null);
    if (!el) return;

    onDown = (ev: MouseEvent) => {
      const t = ev.target as HTMLElement | null;
      if (t && t.closest(".leaflet-popup-close-button")) return;
      requestAnimationFrame(() => bringPopupToFront(marker, map));
    };
    el.addEventListener("mousedown", onDown, true);
    (marker as any).__popupFrontHandler = onDown;
  };

  // Handler for popup close: remove mouse event
  const onClose = () => {
    const popup: any = (marker as any).getPopup?.() ?? (marker as any)._popup;
    const el: HTMLElement | null =
      popup?.getElement?.() ?? (popup?._container as HTMLElement | null);
    const handler = (marker as any).__popupFrontHandler as
      | ((e: MouseEvent) => void)
      | undefined;
    if (el && handler) el.removeEventListener("mousedown", handler, true);
    (marker as any).__popupFrontHandler = undefined;
  };

  marker.on("popupopen", onOpen);
  marker.on("popupclose", onClose);
}

/**
 * For a clustered parent, re-open popups that were previously saved in openIds.
 * Used to restore popup state after cluster spiderfy/unspiderfy.
 */
export function reopenSavedPopupsFor(
  parent: any,
  openIds: Set<string>,
  delay = 200
) {
  const children: any[] = parent?.getAllChildMarkers?.() || [];
  children.forEach((m) => {
    const vid = m.__victimId as string | undefined;
    if (vid && openIds.has(vid)) {
      setTimeout(() => openPopupWithoutPan(m), delay);
    }
  });
}

/**
 * Handles opening/closing popups for all markers in a row.
 * If any popup for the row is open, closes all. Otherwise, opens all.
 */
export function handleOpenRowPopups(
  allMarkers: { marker: L.Marker; group?: L.MarkerClusterGroup }[],
  map: L.Map
) {
  return (ev: Event) => {
    const rowId = (ev as CustomEvent)?.detail?.rowId;
    if (rowId == null) return;
    const byGroup = new Map<L.MarkerClusterGroup | undefined, L.Marker[]>();
    for (const { marker, group } of allMarkers) {
      if ((marker as any).__rowId === rowId) {
        const arr = byGroup.get(group) ?? [];
        arr.push(marker);
        byGroup.set(group, arr);
      }
    }
    if (!byGroup.size) return;
    let anyOpen = false;
    byGroup.forEach((markers) =>
      markers.forEach((m) => {
        const popup = (m as any).getPopup?.() ?? (m as any)._popup;
        if (popup && (map as any).hasLayer(popup)) anyOpen = true;
      })
    );

    // If any popup is open, close all for the row
    if (anyOpen) {
      byGroup.forEach((markers) => markers.forEach((m) => m.closePopup()));
      return;
    }

    // Otherwise, open all popups for the row
    byGroup.forEach((markers, group) => {
      if (!group) {
        markers.forEach((m) =>
          setTimeout(() => openPopupWithoutPan(m), POPUP_REOPEN_DELAY)
        );
        return;
      }
      const parent: any = (group as any).getVisibleParent(markers[0]);
      parent?.spiderfy?.();
      setTimeout(
        () => markers.forEach((m) => openPopupWithoutPan(m)),
        POPUP_REOPEN_DELAY
      );
    });
  };
}